/* eslint-disable @next/next/no-img-element */
import { FaceLandmarker, FilesetResolver, NormalizedLandmark, Matrix, Classifications } from "@mediapipe/tasks-vision";
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import { Facemesh, FacemeshApi, OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useFrame } from "@react-three/fiber";
import THREE, {
    AnimationMixer,
    AxesHelper,
    BufferAttribute,
    BufferGeometry,
    Group,
    LoopRepeat,
    Matrix4,
    Mesh,
    Quaternion,
    Scene,
    TextureLoader,
    Vector3,
} from "three";
import { ProjectedMaterial } from "@lume/three-projected-material";
import test from "node:test";
import { Thread } from "@sentry/browser";
import { DepthOfField, EffectComposer, HueSaturation, Noise } from "@react-three/postprocessing";
import { UserBody } from "@/components/three/UserBody";
import UserNec from "@/components/three/UserNec";
import UserFace from "@/components/three/UserFace";
import UserHead from "@/components/three/UserHead";
import { GroupHead } from "@/components/three/GroupHead";
import { GroupBody } from "@/components/three/GroupBody";
import { maximum } from "@tensorflow/tfjs";
import clsx from "clsx";
import { Store } from "stores/store";
import { inject, observer } from "mobx-react";
import LaserEffect from "../effects/Lasers";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage, getStrapiAssetUrl } from "utils/imageHelpers";
import ExportedImage from "next-image-export-optimizer";
import { Enum_Componentchatmessageparameters_Switchbackground } from "types/strapi";
import { UiState } from "@/components/enums/UiState";
import sendEventToBackend, { use4K } from "@/lib/utils";
import CrossfadeGif from "./CrossfadeGif";

type ProjectionProps = {
    store?: Store;
};

export type UploadImageResponse = {
    filename: string;
    original_name: string;
    size: number;
    mime_type: string;
    url: string;
};

const Projection: React.FunctionComponent<ProjectionProps> = (props) => {
    if (!props.store) {
        throw Error("Store was not setup correctly");
    }

    const {
        chatBackground,
        imagePresentUrl,
        imageAgedUrl,
        updateSelfieImageUrl,
        updateChatProjectionSceneReady,
        loadingLocalized,
        chatSteleLocalized,
        isCancelCallVisible,
        videoTransitionSourceUrl,
        videoMainSourceUrl,
        transitionFinished,
        updateVideoTransitionSourceUrl,
        updateVideoMainSourceUrl,
        updateTransitionFinished,
        resetAll,
        updateFaceNotDetected,
        updateRenderContent,
        textureProjectionCompleted,
        updateTextureProjectionCompleted,
        updateQrCodeImageUrl
    } = props.store;

    const FourK = use4K();

    let faceSolveRunning = false;
    const [faceSolveCompleted, setFaceSolveCompleted] = useState<Boolean>(false);

    let geoUpdateRunning = false;
    const [geoUpdateCompleted, setGeoUpdateCompleted] = useState<Boolean>(false);

    let geoPositioningRunning = false;
    const [geoPositioningCompleted, setGeoPsitioningCompleted] = useState<Boolean>(false);

    let textureProjectionRunning = false;

    const [sceneReady, setSceneReady] = useState<Boolean>(false);

    const helper = useRef<AxesHelper>(null);

    const isStele: boolean = process.env.NEXT_PUBLIC_IS_STELE === "true";

    const selfieBrandingImage = useRef<HTMLImageElement>(null);
    const selfieBackgroundImage = useRef<HTMLImageElement>(null);
    const presentImage = useRef<HTMLImageElement>(null);
    const agedImage = useRef<HTMLImageElement>(null);
    const threeCanvas = useRef<HTMLCanvasElement>(null);
    const threeCamera = useRef<THREE.PerspectiveCamera>(null);
    const threeScene = useRef<THREE.Scene>(new Scene());

    const meshMask = useRef<FacemeshApi>(null);
    const meshMaskImport = useRef<Mesh>(null);
    const meshHead = useRef<Mesh>(null);
    //const meshNec = useRef<Mesh>(null);

    const groupHead = useRef<Group>(null);
    const groupBody = useRef<Group>(null);
    const groupBodyOffset = useRef<Group>(null);

    const animationMixer = useRef<AnimationMixer[]>(new Array<AnimationMixer>());

    const [faceLandmarks, setFaceLandmarks] = useState<NormalizedLandmark[]>();
    const [faceTransform, setFaceTransform] = useState<Matrix>();
    const [faceBlendeshapes, setFaceBlendeshapes] = useState<Classifications>();

    const [focusDistance, setFocusDistance] = useState<number>(0);

    const faceSolve = async function () {
        if (agedImage !== null && agedImage.current && !faceSolveRunning && imageAgedUrl) {
            console.debug("Start Face Solve");
            console.log("agedImage.current ", agedImage.current)
            console.log("imageAgedUrl ", imageAgedUrl)

            // Only do once
            faceSolveRunning = true;

            // Load the Vision File (can be done only once for performance)
            const visionFile = await FilesetResolver.forVisionTasks(
                process.env.NEXT_PUBLIC_BASE_PATH + "/wasm",
            );

            // Create Landmarker Object
            const faceLandmarker = await FaceLandmarker.createFromOptions(visionFile, {
                baseOptions: {
                    modelAssetPath: process.env.NEXT_PUBLIC_BASE_PATH + "/models/face_landmarker.task",
                },
                runningMode: "IMAGE",
                outputFaceBlendshapes: true,
                outputFacialTransformationMatrixes: true,
                // minFaceDetectionConfidence: 0.99,
                // minFacePresenceConfidence: 0.99,
                // minTrackingConfidence: 0.99,
                numFaces: 1,
            });

            // Perform face detection
            console.log("agedImage ", agedImage)
            const faceLandmarkerResult = faceLandmarker.detect(agedImage.current);

            // Check if landmarker result is valid
            if (faceLandmarkerResult.facialTransformationMatrixes.length === 0) {
                console.error("Projection - Face landmarker not identified");
                sendEventToBackend("noFaceDetected", { "process": "projection" })
                updateFaceNotDetected(true)
                //resetAll()
                updateRenderContent(UiState.MASK)
            } else {
                setFaceLandmarks(faceLandmarkerResult.faceLandmarks[0]);
                setFaceTransform(faceLandmarkerResult.facialTransformationMatrixes[0]);
                setFaceBlendeshapes(faceLandmarkerResult.faceBlendshapes[0]);
                setTimeout(() => {
                    console.debug("Finished Face Solve");
                    setFaceSolveCompleted(true);
                }, 1000);
            }

        }
    };

    const geoUpdate = function () {
        if (!geoUpdateRunning && meshMaskImport.current && meshMask.current && meshMask.current.meshRef.current) {
            console.debug("Start Geo Update");

            // Only do once
            geoUpdateRunning = true;

            /*
             * Close Mouth
             */

            console.debug("  Close Mouth");

            // Get the current index array length
            const FaceMaskIndexSize = meshMask.current!.meshRef.current!.geometry.index!.array.length;

            // Create a new Uint16Array to hold the old and new indices
            const indices: Uint16Array = new Uint16Array(FaceMaskIndexSize + 54);

            // Set the old indices into the new array
            indices.set(meshMask.current!.meshRef.current!.geometry.index!.array, 0);

            // Set the new face indices
            indices.set([191, 78, 95], FaceMaskIndexSize + 0);
            indices.set([80, 191, 95], FaceMaskIndexSize + 3);
            indices.set([80, 95, 88], FaceMaskIndexSize + 6);
            indices.set([81, 80, 88], FaceMaskIndexSize + 9);
            indices.set([81, 88, 178], FaceMaskIndexSize + 12);
            indices.set([82, 81, 178], FaceMaskIndexSize + 15);
            indices.set([82, 178, 87], FaceMaskIndexSize + 18);
            indices.set([13, 82, 87], FaceMaskIndexSize + 21);
            indices.set([13, 87, 14], FaceMaskIndexSize + 24);
            indices.set([312, 13, 317], FaceMaskIndexSize + 27);
            indices.set([13, 14, 317], FaceMaskIndexSize + 30);
            indices.set([311, 312, 402], FaceMaskIndexSize + 33);
            indices.set([312, 317, 402], FaceMaskIndexSize + 36);
            indices.set([310, 311, 318], FaceMaskIndexSize + 39);
            indices.set([311, 402, 318], FaceMaskIndexSize + 42);
            indices.set([415, 310, 324], FaceMaskIndexSize + 45);
            indices.set([310, 318, 324], FaceMaskIndexSize + 48);
            indices.set([324, 308, 415], FaceMaskIndexSize + 51);

            console.debug("  Copy Data");

            // Copy Morph Controls from imported mask to the solved
            meshMask.current!.meshRef.current!.morphTargetDictionary = Object.assign(
                {},
                meshMaskImport.current.morphTargetDictionary,
            );
            meshMask.current!.meshRef.current!.morphTargetInfluences =
                meshMaskImport.current.morphTargetInfluences!.slice();

            // Copy Animations from imported mask to the solved
            meshMask.current.meshRef.current.animations = meshMaskImport.current.animations;
            meshMask.current.meshRef.current.name = "face"; // rename to face

            // Remove the 10 unused landmarks
            var arrayPositions = Array.from(meshMask.current.meshRef.current.geometry.attributes.position.array).slice(
                0,
                -30,
            );
            var arrayNormals = Array.from(meshMask.current.meshRef.current.geometry.attributes.normal.array).slice(
                0,
                -15,
            );

            // Assemble Geometry
            const geometry = new BufferGeometry();
            geometry.setAttribute("position", new BufferAttribute(new Float32Array(arrayPositions), 3));
            geometry.setAttribute("normal", new BufferAttribute(new Float32Array(arrayNormals), 3));
            geometry.setIndex(new BufferAttribute(indices, 1));

            // Copy Morph Data
            geometry.morphAttributes["position"] = [];
            geometry.morphAttributes["position"].push(
                meshMaskImport.current.geometry.morphAttributes.position[0].clone(),
            );
            for (let attributeName in meshMaskImport.current.geometry.morphAttributes) {
                if (meshMaskImport.current.geometry.morphAttributes.hasOwnProperty(attributeName)) {
                    geometry.morphAttributes[attributeName] = [];
                    const morphAttributeArray = meshMaskImport.current.geometry.morphAttributes[attributeName];
                    for (let i = 0; i < morphAttributeArray.length; i++) {
                        geometry.morphAttributes[attributeName].push(morphAttributeArray[i].clone());
                    }
                }
            }
            if (meshMaskImport.current.geometry.morphTargetsRelative !== undefined) {
                geometry.morphTargetsRelative = meshMaskImport.current.geometry.morphTargetsRelative;
            }

            // Assign Final Geometry
            meshMask.current!.meshRef.current!.geometry = geometry;
            setTimeout(() => {
                meshMask.current!.meshRef.current!.geometry.setDrawRange(0, geometry.index!.count);
            }, 1000);

            console.debug("Finished Geo Update");
            setGeoUpdateCompleted(true);
            setSceneReady(true)
        }
    };

    const geoPositioning = function () {
        if (
            !geoPositioningRunning &&
            meshMask.current &&
            meshMask.current.meshRef.current &&
            meshHead.current &&
            groupHead.current &&
            groupBodyOffset.current &&
            helper.current
        ) {
            console.debug("Position Geo");

            // Only do once
            geoPositioningRunning = true;

            /*
             * Position Geometry to the Face Mask
             */

            // Get the vertex for scaling
            const vertexTopIndex = 10;
            const vertexBottomIndex = 152;
            const vertexLeftIndex = 127;
            const vertexRightIndex = 356;
            const vertexMouthIndex = 17;

            // Face vertex positions
            const sourceVertices = meshMask.current.meshRef.current.geometry.attributes.position.array;

            // Face source matrix
            const sourceMatrixWorld = meshMask.current.meshRef.current.matrixWorld;

            // Get the position of the Top Index
            const t_x = sourceVertices[vertexTopIndex * 3];
            const t_y = sourceVertices[vertexTopIndex * 3 + 1];
            const t_z = sourceVertices[vertexTopIndex * 3 + 2];
            const worldTopVertex = new Vector3(t_x, t_y, t_z).applyMatrix4(sourceMatrixWorld);

            // Get the position of the Bottom Index
            const b_x = sourceVertices[vertexBottomIndex * 3];
            const b_y = sourceVertices[vertexBottomIndex * 3 + 1];
            const b_z = sourceVertices[vertexBottomIndex * 3 + 2];
            const worldBottomVertex = new Vector3(b_x, b_y, b_z).applyMatrix4(sourceMatrixWorld);

            // Get the position of the Left Index
            const l_x = sourceVertices[vertexLeftIndex * 3];
            const l_y = sourceVertices[vertexLeftIndex * 3 + 1];
            const l_z = sourceVertices[vertexLeftIndex * 3 + 2];
            const worldLeftVertex = new Vector3(l_x, l_y, l_z).applyMatrix4(sourceMatrixWorld);

            // Get the position of the Right Index
            const r_x = sourceVertices[vertexRightIndex * 3];
            const r_y = sourceVertices[vertexRightIndex * 3 + 1];
            const r_z = sourceVertices[vertexRightIndex * 3 + 2];
            const worldRightVertex = new Vector3(r_x, r_y, r_z).applyMatrix4(sourceMatrixWorld);

            // Get the position of the Mouth Index
            const m_x = sourceVertices[vertexMouthIndex * 3];
            const m_y = sourceVertices[vertexMouthIndex * 3 + 1];
            const m_z = sourceVertices[vertexMouthIndex * 3 + 2];
            const worldMouthVertex = new Vector3(m_x, m_y, m_z).applyMatrix4(sourceMatrixWorld);

            // Position Head Mesh
            meshHead.current.position.set(worldTopVertex.x, worldTopVertex.y, worldTopVertex.z);

            // Rotate Head Mesh
            const directionVertical = new Vector3().subVectors(worldTopVertex, worldBottomVertex).normalize();
            const directionHorizontal = new Vector3().subVectors(worldLeftVertex, worldRightVertex).normalize();
            const quaternionVertical = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), directionVertical);
            const quaternionHorizontal = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), directionHorizontal);
            meshHead.current.setRotationFromQuaternion(quaternionVertical);
            // TODO: Horizontal Rotation not correct

            // Scale Mesh
            const headVerticalDistance = worldTopVertex.distanceTo(worldBottomVertex);
            const headHorizontalDistance = worldLeftVertex.distanceTo(worldRightVertex);
            meshHead.current.scale.set(headHorizontalDistance / 0.165, headVerticalDistance / 0.176, 1);

            // Atteach Face and Head to the Head group for Head Animation and relative body position
            meshHead.current.geometry.computeBoundingBox();
            if (meshHead.current.geometry.boundingBox) {
                let headCenter = new Vector3();
                meshHead.current.geometry.boundingBox!.getCenter(headCenter);
                meshHead.current.localToWorld(headCenter);
                groupHead.current.position.copy(headCenter);
                groupHead.current.attach(meshHead.current);
                groupHead.current.attach(meshMask.current.meshRef.current);
                helper.current.position.copy(worldMouthVertex);

                let horizotalLimitOffset = worldMouthVertex.x;
                if (horizotalLimitOffset > 0.03) horizotalLimitOffset = 0.03;
                if (horizotalLimitOffset < -0.03) horizotalLimitOffset = -0.03;
                groupBodyOffset.current.position.set(
                    horizotalLimitOffset,
                    worldMouthVertex.y + 0.08, // Higher is up
                    worldMouthVertex.z + 0.04, // Higher is back
                );
            }

            /*
             * Set vertex positions from head to face edge
             */

            // Source and Target points array - keep in sync
            const SourcePointsIndex = [
                10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176,
                149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109, 10,
            ];
            const TargetPointsIndex = [
                10, 124, 123, 127, 131, 119, 122, 128, 135, 126, 132, 120, 130, 125, 121, 129, 134, 133, 5, 18, 19, 14,
                2, 8, 15, 1, 17, 11, 20, 13, 3, 0, 16, 12, 6, 7, 10,
            ];

            // Get world positions from source points
            let SourcePointsWorld: Vector3[] = [];
            SourcePointsIndex.forEach((point) => {
                SourcePointsWorld.push(
                    new Vector3(
                        sourceVertices[point * 3],
                        sourceVertices[point * 3 + 1],
                        sourceVertices[point * 3 + 2],
                    ).applyMatrix4(sourceMatrixWorld),
                );
            });

            // Inverse transform for world position to head local transform
            const targetMatrixWorldInverse = new Matrix4().copy(meshHead.current.matrixWorld).invert();

            // Setup target vertex array
            let targetVerticesArray = new Float32Array(meshHead.current.geometry.attributes.position.count * 3);
            targetVerticesArray.set(meshHead.current.geometry.attributes.position.array);

            // Set target points positions
            let sourceIndex = 0;
            TargetPointsIndex.forEach((point) => {
                SourcePointsWorld[sourceIndex].applyMatrix4(targetMatrixWorldInverse);
                targetVerticesArray[point * 3] = SourcePointsWorld[sourceIndex].x;
                targetVerticesArray[point * 3 + 1] = SourcePointsWorld[sourceIndex].y;
                targetVerticesArray[point * 3 + 2] = SourcePointsWorld[sourceIndex].z;
                sourceIndex++;
            });

            // Update the target mesh
            meshHead.current.geometry.setAttribute("position", new BufferAttribute(targetVerticesArray, 3));
            meshHead.current.geometry.attributes.position.needsUpdate = true;

            console.debug("Finished Geo Positioning");
            setGeoPsitioningCompleted(true);
        }
    };

    const textureProjection = function () {
        if (
            !textureProjectionRunning &&
            threeCamera.current &&
            agedImage.current &&
            meshMask.current &&
            meshMask.current.meshRef.current &&
            meshHead.current
        ) {
            textureProjectionRunning = true;

            console.debug("Start Projection");

            // Load the aged image as texture
            const agedTexture = new TextureLoader().load(agedImage.current.src);

            // Create a Projection Material for it
            const projectedMaterial1 = new ProjectedMaterial({
                camera: threeCamera.current,
                texture: agedTexture,
                color: "black",
                roughness: 1,
                metalness: 0,
                ior: 1,
                reflectivity: 0,
                transparent: true,
                flatShading: false,
                side: 2,
                envMapIntensity: 0,
            });

            // Make three copies of our material for other objects
            const projectedMaterial2 = projectedMaterial1.clone();
            //const projectedMaterial3 = projectedMaterial1.clone();

            // Active Transparency
            projectedMaterial1.uniforms.backgroundOpacity.value = 0;
            projectedMaterial2.uniforms.backgroundOpacity.value = 0;
            //projectedMaterial3.uniforms.backgroundOpacity.value = 0;

            // Assign the materials to the objects
            meshMask.current.meshRef.current.material = projectedMaterial1;
            meshHead.current.material = projectedMaterial2;
            //meshNec.current.material = projectedMaterial3;

            // Do the Projection
            projectedMaterial1.project(meshMask.current.meshRef.current);
            projectedMaterial2.project(meshHead.current);
            //projectedMaterial3.project(meshNec.current);

            updateTextureProjectionCompleted(true);
        }
    };

    const runAnimation = function () {
        if (
            !textureProjectionRunning &&
            threeCamera.current &&
            meshMask.current &&
            meshMask.current.meshRef.current &&
            groupBody.current &&
            groupHead.current
        ) {
            /*
             * Depth of field Calculation
             * Set Focus for DoF to one of the eyes or maybe average between?
             * FocusDistance is between 0 to 1
             * Relative to within the camera near and far range
             */

            console.debug("Start DoF Calculation");

            // Get Eye World Position
            const worldPositionEye = new Vector3(0);
            meshMask.current.eyeRightRef.current?.irisDirRef.current?.getWorldPosition(worldPositionEye);

            // Get Camera World Position
            const worldPositionCamera = new Vector3(0);
            threeCamera.current.getWorldPosition(worldPositionCamera);

            // Calculate Distance between Eye and Camera
            const distanceToEye = worldPositionCamera.z + worldPositionEye.z;

            // Calculate Focal Range near to far
            const distanceToClipping = threeCamera.current.far - threeCamera.current.near;

            // Calculate Result
            setFocusDistance((distanceToEye - threeCamera.current.near) / distanceToClipping);

            /*
             * Animation
             */

            console.debug("Start Animation");

            // Copy Animation from gltf to body group
            groupBody.current.animations = GroupBody();
            groupBody.current.name = "root-body";

            // Copy Animation from gltf to head group
            groupHead.current.animations = GroupHead();
            groupHead.current.name = "root-head";

            // Create body animation mixer
            const bodyMixer = new AnimationMixer(groupBody.current);
            groupBody.current.animations.forEach((clip) => {
                //bodyMixer.clipAction(clip).play().setLoop(LoopRepeat, Infinity);
            });

            // Create head animation mixer
            const headMixer = new AnimationMixer(groupHead.current);
            groupHead.current.animations.forEach((clip) => {
                headMixer.clipAction(clip).setEffectiveWeight(0.4).play().setLoop(LoopRepeat, Infinity);
                //headMixer.clipAction(clip).play().setLoop(LoopRepeat, Infinity);
            });

            // Create face animation mixer
            const faceMixer = new AnimationMixer(meshMask.current.meshRef.current);
            meshMask.current.meshRef.current.animations.forEach((clip) => {
                faceMixer.clipAction(clip).play().setLoop(LoopRepeat, Infinity);
            });

            // Assign mixers
            animationMixer.current.push(bodyMixer);
            animationMixer.current.push(headMixer);
            animationMixer.current.push(faceMixer);

            setSceneReady(true);
        }
    };

    const getImage = function () {
        if (threeCanvas.current) {
            // Probably just return the threeCanvas.current.toDataURL('image/png') then combine with background and jung user image?!
            const link = document.createElement("a");
            link.setAttribute("download", `Screenshot-${Date.now()}.png`);
            link.setAttribute(
                "href",
                threeCanvas.current.toDataURL("image/png").replace("image/png", "image/octet-stream"),
            );
            link.click();
            link.remove();
        }
    };

    // must be within Canvas component and must be JSX compatible
    const FrameTimer: FunctionComponent = function (props: any, deprecatedLegacyContext?: any) {
        useFrame((state, delta) => {
            animationMixer.current.forEach((mixer) => {
                mixer.update(delta);
            });
        });
        return null;
    };

    useEffect(() => {
        faceSolve();
    }, [agedImage]);

    useEffect(() => {
        geoUpdate();
    }, [faceSolveCompleted]);

    useEffect(() => {
        geoPositioning();
    }, [geoUpdateCompleted]);

    useEffect(() => {
        textureProjection();
    }, [geoPositioningCompleted]);

    useEffect(() => {
        runAnimation();
    }, [textureProjectionCompleted]);

    useEffect(() => {
        setTimeout(() => { updateChatProjectionSceneReady(!!sceneReady) }, 1000)
    }, [sceneReady]);

    useEffect(() => {
        console.debug("checkpoint selfie", sceneReady, chatSteleLocalized);
        if (sceneReady && chatSteleLocalized) {
            const timer = setTimeout(() => {
                console.debug(
                    "taking selfie",
                    threeCanvas.current,
                    presentImage.current,
                    selfieBackgroundImage.current,
                    selfieBrandingImage.current,
                );

                if (
                    threeCanvas.current &&
                    presentImage.current &&
                    selfieBackgroundImage.current &&
                    selfieBrandingImage.current
                ) {

                    const selfieCanvas = document.createElement("canvas");
                    selfieCanvas.width = threeCanvas.current.width;
                    selfieCanvas.height = FourK ? 2520 : 630;
                    const selfieContext = selfieCanvas.getContext("2d");
                    if (selfieContext) {

                        const h = selfieCanvas.height;
                        const w = selfieCanvas.width;
                        const borderWidth = 60;
                        const bottomBorderWidth = borderWidth * 8;

                        // Draw everything on the original canvas as before
                        selfieContext.drawImage(selfieBackgroundImage.current, 0, 0, w, h);
                        selfieContext.drawImage(threeCanvas.current, w / 4, h - threeCanvas.current.height);
                        selfieContext.drawImage(presentImage.current, -w / 4, h - threeCanvas.current.height, h / 2, w / 2);
                        //selfieContext.drawImage(selfieBrandingImage.current, w - 1030, h - 362, 1030, 362);

                        // Create a new canvas for the bordered version
                        const borderedCanvas = document.createElement("canvas");
                        borderedCanvas.width = w + borderWidth + borderWidth;
                        borderedCanvas.height = h + borderWidth + bottomBorderWidth;
                        const borderedContext = borderedCanvas.getContext("2d");

                        if (borderedContext) {
                            // Add borders
                            borderedContext.fillStyle = 'white';
                            borderedContext.fillRect(0, 0, borderedCanvas.width, borderedCanvas.height);
                            borderedContext.drawImage(selfieCanvas, borderWidth, borderWidth);

                            // Add text overlay
                            borderedContext.fillStyle = '#131514'; // Neue-Leben-Gray
                            borderedContext.textAlign = 'center';

                            // First line: "Liebe Grüße aus deiner Zukunft"
                            borderedContext.font = '160px "DeliciousHandrawn-Regular"';
                            borderedContext.fillText(
                                '"Liebe Grüße aus deiner Zukunft"',
                                borderedCanvas.width / 2, // center horizontally
                                borderedCanvas.height - 240 // position from bottom border
                            );

                            // Second line: "triffdeinezukunft.de"
                            borderedContext.font = '100px "FrutigerLTCom-Bold"';
                            borderedContext.fillText(
                                "TriffDeineZukunft.de",
                                borderedCanvas.width / 2, // center horizontally
                                borderedCanvas.height - 100 // position from bottom border
                            );

                            // Use the original canvas for the dataUrl
                            const dataUrl = selfieCanvas.toDataURL("image/png");
                            updateSelfieImageUrl(dataUrl);
                            console.log("ready");

                            // Use the bordered canvas for the upload
                            borderedCanvas.toBlob((blob) => {
                                if (blob) {
                                    const formData = new FormData();
                                    formData.append("file", blob, "selfie.jpg");

                                    if (!process.env.NEXT_PUBLIC_FILE_UPLOAD_SERVICE_URL) {
                                        throw Error("NEXT_PUBLIC_FILE_UPLOAD_SERVICE_URL not set");
                                    }

                                    fetch(process.env.NEXT_PUBLIC_FILE_UPLOAD_SERVICE_URL, {
                                        method: "POST",
                                        body: formData,
                                    })
                                        .then(response => response.json() as Promise<UploadImageResponse>)
                                        .then(result => {
                                            const uploadedImageUrl = result.url;
                                            if (uploadedImageUrl) {
                                                updateQrCodeImageUrl(uploadedImageUrl);
                                            }
                                        })
                                        .catch(error => {
                                            console.error("Error uploading selfie:", error);
                                        });
                                }
                            }, "image/jpeg");
                        }
                    }
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [sceneReady, chatSteleLocalized, imageAgedUrl, imagePresentUrl]);

    const baseBackgroundRef = useRef<HTMLVideoElement>(null);
    const videoTransitionRef = useRef<HTMLVideoElement>(null);
    const videoMainRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        console.log("ChatBackground: ", chatBackground);
        if (baseBackgroundRef.current && videoTransitionRef.current && videoMainRef.current) {
            if (
                chatBackground === "" ||
                chatBackground === Enum_Componentchatmessageparameters_Switchbackground.FaceToFace
            ) {
                if (baseBackgroundRef.current.paused) {
                    baseBackgroundRef.current.play();
                }

                videoTransitionRef.current.pause();
                videoMainRef.current.pause();
                videoTransitionRef.current.currentTime = 0;
                videoMainRef.current.currentTime = 0;

                updateVideoTransitionSourceUrl("");
                updateVideoMainSourceUrl("");
            } else {
                updateTransitionFinished(false);
                baseBackgroundRef.current.autoplay = false;
                baseBackgroundRef.current.pause();

                console.log("ChatBackground paused");

                if (chatBackground === Enum_Componentchatmessageparameters_Switchbackground.InsideHome) {
                    updateVideoTransitionSourceUrl("videos/cameraswitch/Living_room_neutral_transition_1_4k.mp4");
                    updateVideoMainSourceUrl("videos/cameraswitch/Living_room_neutral_loop_1_4k.mp4");
                } else if (chatBackground === Enum_Componentchatmessageparameters_Switchbackground.OutsideHome) {
                    updateVideoTransitionSourceUrl("videos/cameraswitch/City_view_neutral_transition_1_4k.mp4");
                    updateVideoMainSourceUrl("videos/cameraswitch/City_view_neutral_loop_1_4k.mp4");
                }

                videoTransitionRef.current.play();
            }
        }
    }, [chatBackground]);

    useEffect(() => {
        if (baseBackgroundRef.current && videoTransitionRef.current && videoMainRef.current) {
            if (isCancelCallVisible) {
                baseBackgroundRef.current.pause();
                videoTransitionRef.current.pause();
                videoMainRef.current.pause();
            } else {
                if (
                    chatBackground === "" ||
                    chatBackground === Enum_Componentchatmessageparameters_Switchbackground.FaceToFace
                ) {
                    baseBackgroundRef.current.play();
                } else {
                    if (transitionFinished) {
                        videoMainRef.current.play();
                    } else {
                        videoTransitionRef.current.play();
                    }
                }
            }
        }
    }, [isCancelCallVisible]);


    return (
        <div className="relative w-screen h-[100dvh] min-h-[100svh]">
            <img
                //src={process.env.NEXT_PUBLIC_BASE_PATH + "images/test/test_adrian.png"}
                //src={process.env.NEXT_PUBLIC_BASE_PATH + "images/test/test_lena.png"}
                //src={process.env.NEXT_PUBLIC_BASE_PATH + "images/test/test_evi.png"}
                // src={process.env.NEXT_PUBLIC_BASE_PATH + "images/test/test_lena-cc.png"}
                src={imageAgedUrl}
                alt=""
                className="top-0 right-0 fixed opacity-0 pointer-events-none"
                crossOrigin="anonymous"
                ref={agedImage}
            />

            <img // TODO: Replace this with the actual background removed cam image coming from the backend service
                src={imagePresentUrl}
                alt=""
                className="top-0 left-0 fixed opacity-0 pointer-events-none"
                crossOrigin="anonymous"
                ref={presentImage}
            />

            {/* {chatSteleLocalized?.selfieBackground && (
                <ExportedImage
                    src={getImageUrlFromGeneralImage(chatSteleLocalized?.selfieBackground)}
                    alt={getAltTextFromGeneralImage(chatSteleLocalized?.selfieBackground)}
                    className="top-0 left-0 fixed opacity-0 pointer-events-none"
                    crossOrigin="anonymous"
                    ref={selfieBackgroundImage}
                    width={1400}
                    height={1633}
                />
            )} */}

            {chatSteleLocalized?.selfieBranding && (
                <ExportedImage
                    src={getImageUrlFromGeneralImage(chatSteleLocalized?.selfieBranding)}
                    alt={getAltTextFromGeneralImage(chatSteleLocalized?.selfieBranding)}
                    className="top-0 left-0 fixed opacity-0 pointer-events-none"
                    crossOrigin="anonymous"
                    ref={selfieBrandingImage}
                    width={740}
                    height={436}
                />
            )}

            {/*<img
                src={selfieImageUrl || ""} // debug view for selfie
                alt=""
                className="top-0 left-0 z-50 fixed opacity-1"
            />*/}

            <div className="top-0 left-0 absolute flex justify-center items-start w-full [aspect-ratio:9/16] | ProjectionBackground">
                <div
                    className={clsx(
                        "top-0 absolute w-full max-w-[756px] h-full max-h-[100dvh] aspect-[9/16] transition-all duration-1000 4k:max-w-min",
                        "[mask-repeat:no-repeat]",
                        "[mask-origin:content-box]",
                        "[mask-position:50%_50%]",
                        sceneReady ? "[mask-size:400%_400%]" : "[mask-size:0px_0px]",
                    )}
                    style={{ maskImage: 'url("' + process.env.NEXT_PUBLIC_BASE_PATH + '/images/stele/videoMask.png")' }}
                >
                    {chatSteleLocalized?.projectionBackgroundVideo !== undefined &&
                        chatSteleLocalized?.projectionBackgroundVideo !== null && (

                            <video
                                autoPlay
                                muted
                                loop
                                playsInline
                                disablePictureInPicture
                                className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
                                ref={baseBackgroundRef}
                            >
                                {chatSteleLocalized?.projectionBackgroundVideo.image?.data?.attributes?.url !==
                                    undefined && (
                                        <source
                                            src={getStrapiAssetUrl(
                                                chatSteleLocalized?.projectionBackgroundVideo.image?.data?.attributes?.url,
                                            )}
                                            type="video/mp4"
                                        />
                                    )}
                            </video>


                        )}

                    <video
                        src={videoTransitionSourceUrl}
                        ref={videoTransitionRef}
                        autoPlay
                        muted
                        playsInline
                        disablePictureInPicture
                        className={clsx(
                            "top-0 left-0 absolute w-full h-full pointer-events-none",
                            (chatBackground === "" ||
                                chatBackground === Enum_Componentchatmessageparameters_Switchbackground.FaceToFace) &&
                            "hidden",
                        )}
                        onEnded={() => {
                            if (videoMainRef.current) {
                                updateTransitionFinished(true);
                                videoMainRef.current.pause();
                                videoMainRef.current.currentTime = 0;
                                videoMainRef.current.loop = true;
                                videoMainRef.current.play();
                            }
                        }}
                    />
                    <video
                        src={videoMainSourceUrl}
                        ref={videoMainRef}
                        muted
                        loop
                        playsInline
                        disablePictureInPicture
                        className={clsx(
                            "top-0 left-0 absolute w-full h-full pointer-events-none",
                            (chatBackground === "" ||
                                chatBackground === Enum_Componentchatmessageparameters_Switchbackground.FaceToFace ||
                                !transitionFinished) &&
                            "hidden",
                        )}
                    />

                    <div
                        className={clsx(
                            "left-0 absolute flex justify-center items-center w-full flyUpContainer",
                            sceneReady ? "[aspect-ratio:1/1.3333]" : "[aspect-ratio:1/1]",
                            chatBackground !== "" &&
                            chatBackground !== Enum_Componentchatmessageparameters_Switchbackground.FaceToFace &&
                            "hidden",
                        )}
                    >
                        <div className="relative flex bg-whiteShade-20 shadow-projection-bubble 4k:shadow-projection-bubble-4k drop-shadow-2xl backdrop-blur-[60px] 4k:backdrop-blur-[240px] rounded-full 4k:rounded-full shrinkSizeContainer cssSizes">
                            <img
                                src="./Overlay_White.png"
                                alt=""
                                className="top-0 left-0 z-50 absolute opacity-1 rounded-full w-full h-full"
                            />
                            <Canvas
                                ref={threeCanvas}
                                scene={threeScene.current}
                                gl={{ antialias: true, toneMapping: 0, preserveDrawingBuffer: true }}
                                linear={true}
                                className="block rounded-full 4k:rounded-full"
                            >
                                <FrameTimer />

                                <PerspectiveCamera
                                    ref={threeCamera}
                                    position={[0, 0, 0]}
                                    rotation={[0, Math.PI, 0]}
                                    fov={65}
                                    near={0.01}
                                    far={2}
                                    aspect={1}
                                    makeDefault
                                />

                                {/*
                                <OrbitControls target={[0, 0, 0.4]} />
                                */}

                                <ambientLight intensity={1} />

                                {/* Group for all geo for a gloabal offset */}
                                <group ref={groupBody}>
                                    {/* Group for head and face rotation, attachment happens in code later */}
                                    <group ref={groupHead} />

                                    <Facemesh
                                        ref={meshMask}
                                        points={faceLandmarks}
                                        facialTransformationMatrix={faceTransform}
                                        faceBlendshapes={faceBlendeshapes}
                                        position={0}
                                        rotation={[0, 0, 0]}
                                        rotation-z={Math.PI}
                                        scale={1}
                                        eyes={true}
                                        eyesAsOrigin={false}
                                        offset={true}
                                        //offsetScalar={80}
                                        depth={0.15}
                                        debug={false}
                                    ></Facemesh>

                                    <UserFace
                                        ref={meshMaskImport}
                                        position={[0.25, 0, 0.4]}
                                        rotation={[0, 0, Math.PI]}
                                        scale={0}
                                    />

                                    <axesHelper ref={helper} scale={0} />

                                    <UserHead
                                        ref={meshHead}
                                        position={[0, 0, 0]}
                                        rotation={[0, 0, 0]}
                                        scale={[1, 1, 1]}
                                    />

                                    <group ref={groupBodyOffset} position={[0, 0, 0]} rotation={[0, 0, 0]} scale={1}>

                                    </group>
                                </group>

                                <EffectComposer>
                                    <Noise opacity={0.05} />
                                    <HueSaturation saturation={-0.25} />
                                    {/* <DepthOfField focusDistance={focusDistance} focalLength={0.005} bokehScale={1} /> */}
                                </EffectComposer>
                            </Canvas>
                        </div>
                    </div>
                    <div className="bottom-0 absolute bg-gradient-bottom-chat opacity-80 w-full h-[50%] pointer-events-none"></div>
                    {/*<div className="bottom-0 absolute bg-[rgb(17,41,45)] w-full h-[36%]"></div>*/}

                    <div className="z-[-100] absolute w-0 h-0 pointer-events-none">
                        <CrossfadeGif
                            imageAgedUrl={imageAgedUrl ?? ""}
                            imagePresentUrl={imagePresentUrl ?? ""}
                            durationMs={5000}
                            imageAgedBgUrl="/Neutral.png"
                        />
                    </div>
                </div>
            </div>
            <div className="invisible absolute font-deliciousHandrawn">.</div>
        </div>
    );
};

export default inject("store")(observer(Projection));
